

import type { ChangeVerifier, CountTextFn, VerifyFn } from "./changeLog";
import {
  getWordComments,
  getWordOccurrences,
  getWordTrackingEnabled,
  hasWordBates,
  hasWordPageNumbers,
  hasWordTableOfContents,
} from "./wordDocument";

/** How many copies of `text` the document holds now — the same search the
 *  textPresent check runs, counted (AIVA-8046, see verifyWordChangeLog). */
export const countWordText: CountTextFn = async (text: string): Promise<number | null> => {
  const occ = await getWordOccurrences(text, 0);
  return occ === null ? null : occ.count;
};

export const verifyWordChange: VerifyFn = async (v: ChangeVerifier): Promise<boolean> => {
  switch (v.kind) {
    case "textPresent": {
      // count === 0 → the inserted text is no longer in the body (undone).
      // null → the search couldn't run; keep the record.
      const occ = await getWordOccurrences(v.text, 0);
      return occ === null ? true : occ.count > 0;
    }
    case "toc": {
      const has = await hasWordTableOfContents();
      return has ?? true;
    }
    case "pageNumbers": {
      const has = await hasWordPageNumbers(v.position);
      return has ?? true;
    }
    case "bates": {
      const has = await hasWordBates(v.prefix, v.position);
      return has ?? true;
    }
    case "tracking": {
      // Tracking is a toggle, not an accumulation: the record holds iff the live
      // mode still matches what AIVA set. If the user (or an undo) flipped it
      // back, current !== enabled and the entry drops.
      const current = await getWordTrackingEnabled();
      return current === null ? true : current === v.enabled;
    }
    case "comment": {
      const comments = await getWordComments();
      if (comments === null) return true;
      const match = comments.find((c) => c.id === v.id);
      if (!match) return false;
      return v.resolved === undefined ? true : match.resolved === v.resolved;
    }
  }
};
